
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Category, Subcategory } from "@/types/category";
import { useCategoriesQuery } from "@/hooks/useCategoriesQuery";

// This is a compatibility wrapper around the React Query implementation
export const useCategories = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [subcategoriesByParent, setSubcategoriesByParent] = useState<{[key: number]: Subcategory[]}>({});
  const { toast } = useToast();
  
  // Use the React Query hook internally
  const { 
    data: categoriesData, 
    isLoading: queryLoading, 
    isError,
    error,
    refetch 
  } = useCategoriesQuery();

  useEffect(() => {
    if (categoriesData) {
      setCategories(categoriesData.categories || []);
      setSubcategories(categoriesData.subcategories || []);
      
      // Organize subcategories by parent category
      const subsByParent: {[key: number]: Subcategory[]} = {};
      categoriesData.subcategories.forEach(sub => {
        if (sub.parent_category) {
          if (!subsByParent[sub.parent_category]) {
            subsByParent[sub.parent_category] = [];
          }
          subsByParent[sub.parent_category].push(sub);
        }
      });
      
      setSubcategoriesByParent(subsByParent);
      setLoading(false);
    } else if (!queryLoading && isError) {
      console.error("Error in useCategories:", error);
      setLoading(false);
    } else {
      setLoading(queryLoading);
    }
  }, [categoriesData, queryLoading, isError, error]);

  // Provide a more direct fetch method for backward compatibility
  const fetchCategories = async () => {
    try {
      setLoading(true);
      await refetch();
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error 
          ? `Failed to load categories: ${error.message}` 
          : "Failed to load categories. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    categories,
    subcategories,
    subcategoriesByParent,
    refreshCategories: fetchCategories
  };
};
